import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut as firebaseSignOut, onAuthStateChanged, User, setPersistence, browserLocalPersistence, linkWithCredential, EmailAuthProvider } from 'firebase/auth';
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentSingleTabManager,
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  deleteDoc,
  onSnapshot, 
  Unsubscribe, 
  collection, 
  getDocs,
  query,
  where,
  orderBy,
  limit,
  runTransaction,
  writeBatch
} from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
// Firebase Functions関連のインポートは一時的に削除

// Firebase設定（実際の設定）
const firebaseConfig = {
  apiKey: "AIzaSyDi97jKDPgqJ7ZvxawmtW7VUxSdGkJgNu0",
  authDomain: "ai-headshot-generator-e6f23.firebaseapp.com",
  projectId: "ai-headshot-generator-e6f23",
  storageBucket: "ai-headshot-generator-e6f23.firebasestorage.app",
  messagingSenderId: "951528767582",
  appId: "1:951528767582:web:af08061118efb340d4f4cf",
  measurementId: "G-LZ3C5293Q7"
};

// Stripe設定（本番環境）
const STRIPE_CONFIG = {
  publishableKey: "pk_live_51SAVwbLVferAdY9v3YaRfLpfropM9r2aWl2DvPBaHxvlerM0SD3AVNU9cAQrrESJMFfqL8k95x2DOydMdtLRsLJ600SEokyUTM",
  priceIds: {
    standard: "price_1SFtDxLVferAdY9vYX7AxEht",
    pro: "price_1SFtEZLVferAdY9vW3LArvGA",
    pro_max: "price_1SFtFSLVferAdY9vmdAekB4M"
  }
};

// Firebase初期化（エラーハンドリング付き）
let app, auth, db, storage;

try {
  console.log('🔧 Firebase初期化開始...');
  
  try {
    app = initializeApp(firebaseConfig);
    console.log('✅ Firebase App初期化完了');
  } catch (appError) {
    console.error('❌ Firebase App初期化エラー:', appError);
    throw appError;
  }
  
  try {
    auth = getAuth(app);
    console.log('✅ Firebase Auth初期化完了');
    
    // 認証永続化を有効化（ブラウザ再起動後も匿名ユーザーを保持）
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.warn('認証永続化設定エラー:', error);
    });
  } catch (authError) {
    console.error('❌ Firebase Auth初期化エラー:', authError);
    throw authError;
  }
  
  try {
    storage = getStorage(app);
    console.log('✅ Firebase Storage初期化完了');
  } catch (storageError) {
    console.error('❌ Firebase Storage初期化エラー:', storageError);
    // Storageの初期化エラーは致命的ではないので続行
    storage = null;
  }
  
  try {
    // Firestore初期化
    // forceLongPolling は書き込みがタイムアウトしやすいため使わない
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        cacheSizeBytes: 40 * 1024 * 1024,
      }),
      ignoreUndefinedProperties: true,
    });
    console.log('✅ Firebase Firestore初期化完了');
  } catch (firestoreError) {
    console.error('❌ Firebase Firestore初期化エラー:', firestoreError);
    // Firestoreの初期化エラーは致命的ではないので続行
    db = null;
  }
  
  console.log('🎉 Firebase全サービス初期化完了');
} catch (error) {
  console.error('❌ Firebase初期化エラー:', error);
  console.error('❌ エラー詳細:', {
    name: error?.name,
    message: error?.message,
    stack: error?.stack
  });
  // 開発モードではFirebaseなしで動作を試みる
  try {
    auth = null;
    db = null;
    storage = null;
    console.warn('⚠️ Firebaseなしで動作を試みます（一部機能が制限されます）');
  } catch (fallbackError) {
    console.error('❌ フォールバック処理エラー:', fallbackError);
  }
}

// Cloud Functions関連のコードは一時的に削除（Firestore直接アクセスを使用）


// Firestore接続テスト用の関数（タイムアウト付き）
export const testFirestoreConnection = async (): Promise<boolean> => {
  if (!db) {
    console.error('Firestore not initialized');
    return false;
  }
  
  try {
    console.log('Testing Firestore connection...');
    
    // タイムアウト付きでテスト実行
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Connection timeout')), 5000);
    });
    
    const testPromise = (async () => {
      const testDoc = doc(db, 'test', 'connection');
      await setDoc(testDoc, { timestamp: Date.now() });
    })();
    
    await Promise.race([testPromise, timeoutPromise]);
    console.log('Firestore connection test: SUCCESS');
    return true;
  } catch (error) {
    console.error('Firestore connection test: FAILED', error);
    return false;
  }
};

// ユーザープラン情報を手動で確認する関数（タイムアウト付き）
export const checkUserPlanManually = async (uid: string): Promise<UserPlan | null> => {
  if (!db) {
    console.error('Firestore not initialized');
    return null;
  }
  
  try {
    console.log('Manually checking user plan for:', uid);
    
    // タイムアウト付きで実行
    const timeoutPromise = new Promise<UserPlan | null>((_, reject) => {
      setTimeout(() => reject(new Error('Manual check timeout')), 8000);
    });
    
    const checkPromise = (async () => {
      const userDoc = doc(db, 'users', uid);
      const userSnapshot = await getDoc(userDoc);
      
      if (userSnapshot.exists()) {
        const planData = userSnapshot.data() as UserPlan;
        console.log('Manual plan check: SUCCESS', planData);
        return planData;
      } else {
        console.log('Manual plan check: User document does not exist');
        return null;
      }
    })();
    
    const result = await Promise.race([checkPromise, timeoutPromise]);
    return result;
  } catch (error) {
    console.error('Manual plan check: FAILED', error);
    return null;
  }
};

// オフライン対応のプラン管理は UserPlan / addCreditsRestToPlan 定義後に配置

export { auth, db, functions };

// Google認証プロバイダー
const googleProvider = new GoogleAuthProvider();

// 毎回アカウント選択を強制（自動ログインを防ぐ）
googleProvider.setCustomParameters({
  prompt: "select_account"   // 毎回アカウント選択を出す
});

// ユーザープラン情報の型定義
export interface UserPlan {
  plan: 'free' | 'standard' | 'pro' | 'pro_max';
  purchaseDate?: string;
  expiryDate?: string;
  stripeCustomerId?: string;
  paypayCustomerId?: string;
  creditsUsed?: number; // 使用済みクレジット数（後方互換性のため残す）
  creditsRest?: number; // 残りクレジット数（新規追加）
  lastUpdated?: string;
  isSubscription?: boolean;
}

// プランに応じたクレジット上限を取得
const getCreditLimit = (plan: string): number => {
  switch (plan) {
    case 'free': return 3;
    case 'standard': return 10;
    case 'pro': return 25;
    case 'pro_max': return 200;
    default: return 3;
  }
};

// creditsRestを計算するヘルパー関数
export const calculateCreditsRest = (userPlan: UserPlan): number => {
  const limit = getCreditLimit(userPlan.plan);
  const used = userPlan.creditsUsed || 0;
  return Math.max(0, limit - used);
};

// UserPlanにcreditsRestを追加するヘルパー関数（FirestoreにcreditsRestがある場合はそれを優先、無い場合のみ計算）
export const addCreditsRestToPlan = (userPlan: UserPlan): UserPlan => {
  const rest =
    typeof userPlan.creditsRest === 'number'
      ? Math.max(0, userPlan.creditsRest)
      : calculateCreditsRest(userPlan);
  return {
    ...userPlan,
    creditsRest: rest
  };
};

// オフライン対応のプラン管理
export const getOfflineUserPlan = (uid: string): UserPlan | null => {
  try {
    const stored = localStorage.getItem(`userPlan_${uid}`);
    if (stored) {
      const planData = JSON.parse(stored) as UserPlan;
      return addCreditsRestToPlan(planData);
    }
  } catch (error) {
    console.warn('Failed to load offline plan data:', error);
  }
  return null;
};

export const clearOfflineUserPlan = (uid: string): void => {
  try {
    localStorage.removeItem(`userPlan_${uid}`);
  } catch (error) {
    console.warn('Failed to clear offline plan data:', error);
  }
};

export const setOfflineUserPlan = (uid: string, plan: UserPlan): void => {
  try {
    localStorage.setItem(`userPlan_${uid}`, JSON.stringify(addCreditsRestToPlan(plan)));
  } catch (error) {
    console.warn('Failed to save offline plan data:', error);
  }
};

// 匿名ログイン機能は廃止 - ログイン必須に変更

// 環境判定（アグレッシブめ）
const shouldUseRedirect = () => {
  const ua = navigator.userAgent;
  const inApp = /Line|Instagram|FBAN|FBAV|Twitter|MicroMessenger/i.test(ua);
  const iOSWebView = /iPhone|iPad|iPod/.test(ua) && !/Safari/i.test(ua);
  const crossIso = (window as any).crossOriginIsolated === true; // COOP/COEP 下の可能性大
  // iframe内 or opener 不明などもリダイレクト推奨
  const inIframe = window.self !== window.top;
  
  const shouldRedirect = inApp || iOSWebView || crossIso || inIframe;
  
  console.log('🔍 環境判定:', {
    userAgent: ua,
    inApp,
    iOSWebView,
    crossIso,
    inIframe,
    shouldUseRedirect: shouldRedirect
  });
  
  return shouldRedirect;
};

// .env で強制切替できるように
const FORCE_REDIRECT = (import.meta as any).env?.VITE_AUTH_FORCE_REDIRECT === '1';

// 落ちにくい Google ログイン関数（ポップアップ→自動フォールバック）
export const signInWithGoogleRobust = async (): Promise<User> => {
  try {
    console.log('🔍 Firebase - Googleログイン開始（自動フォールバック）');
    console.log('🔍 Auth状態確認:', {
      auth: !!auth,
      authType: typeof auth,
      authConstructor: auth?.constructor?.name
    });
    
    if (!auth) {
      console.error('❌ Firebase認証が初期化されていません');
      throw new Error('Firebase認証が初期化されていません');
    }
    
    const provider = new GoogleAuthProvider();

    // 1) 事前判定 or 環境変数でリダイレクトへ
    if (FORCE_REDIRECT || shouldUseRedirect()) {
      console.log('🔍 事前判定でリダイレクト方式を使用');
      await signInWithRedirect(auth, provider);
      return null as any; // 実際の結果は handleAuthRedirectResult で取得
    }

    // 2) まずはポップアップを試す → ダメなら自動でリダイレクト
    try {
      console.log('🔍 ポップアップ方式を試行');
      const result = await signInWithPopup(auth, provider);
      console.log('🔍 Firebase - Googleログイン成功（ポップアップ）:', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });
      
      // 新規ユーザーのデフォルトプランを設定
      if (db) {
        const userDoc = doc(db, 'users', result.user.uid);
        const userSnapshot = await getDoc(userDoc);
        
        if (!userSnapshot.exists()) {
          console.log('🔍 新規Googleユーザー検出（Robust）、デフォルトプランを設定');
          const defaultPlan: UserPlan = {
            plan: 'free',
            creditsUsed: 0,
            creditsRest: 3,
            purchaseDate: new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          };
          await setDoc(userDoc, defaultPlan);
          console.log('✅ Google新規ユーザーのデフォルトプラン設定完了（Robust）');
        }
      }
      
      return result.user;
    } catch (e: any) {
      const msg = String(e?.message || e);
      console.log('🔍 ポップアップ失敗、エラーメッセージ:', msg);
      
      // COOP/COEP, ポップアップブロック, 3rd-party cookie/ITP あたりは全部リダイレクトへ
      if (
        /Cross-Origin-Opener-Policy|COOP|popup.*(blocked|closed)|3rd.*cookie|browser.*prevented/i.test(
          msg
        )
      ) {
        console.log('🔍 ポップアップ失敗を検出、リダイレクト方式に自動フォールバック');
        await signInWithRedirect(auth, provider);
        return null as any; // 実際の結果は handleAuthRedirectResult で取得
      }
      
      // 他のエラーはそのまま
      console.error('🔍 ポップアップ失敗（フォールバック対象外）:', e);
      throw e;
    }
  } catch (error) {
    console.error('Googleログインエラー:', error);
    throw error;
  }
};

// リダイレクト結果を処理する関数
export const handleAuthRedirectResult = async (): Promise<User | null> => {
  try {
    if (!auth) {
      console.warn('Firebase認証が初期化されていません');
      return null;
    }
    
    const result = await getRedirectResult(auth);
    if (result?.user) {
      console.log('✅ Google redirect login success', {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName
      });
      return result.user;
    }
    return null;
  } catch (error) {
    console.error('Google redirect result error:', error);
    return null;
  }
};

// Googleログイン（既存関数）
export const signInWithGoogle = async (): Promise<User> => {
  try {
    console.log('🔍 Firebase - Googleログイン開始（アカウント選択強制）');
    if (!auth) {
      throw new Error('Firebase認証が初期化されていません');
    }
    
    // アカウント選択を強制するプロバイダーを使用
    const result = await signInWithPopup(auth, googleProvider);
    console.log('🔍 Firebase - Googleログイン成功:', {
      uid: result.user.uid,
      email: result.user.email,
      displayName: result.user.displayName
    });
    
    // 新規ユーザーのデフォルトプランを設定
    if (db) {
      const userDoc = doc(db, 'users', result.user.uid);
      const userSnapshot = await getDoc(userDoc);
      
      if (!userSnapshot.exists()) {
        console.log('🔍 新規Googleユーザー検出、デフォルトプランを設定');
        const defaultPlan: UserPlan = {
          plan: 'free',
          creditsUsed: 0,
          creditsRest: 3,
          purchaseDate: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        };
        await setDoc(userDoc, defaultPlan);
        console.log('✅ Google新規ユーザーのデフォルトプラン設定完了');
      }
    }
    
    return result.user;
  } catch (error) {
    console.error('Googleログインエラー:', error);
    throw error;
  }
};

// メール/パスワードでログイン
export const signInWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    console.log('🔍 Firebase - メールログイン開始');
    if (!auth) {
      throw new Error('Firebase認証が初期化されていません');
    }
    
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.log('🔍 Firebase - メールログイン成功:', {
      uid: result.user.uid,
      email: result.user.email
    });
    
    return result.user;
  } catch (error: any) {
    console.error('メールログインエラー:', error);
    throw new Error(getSignInErrorMessage(error));
  }
};

const getSignInErrorMessage = (error: any): string => {
  const code = error?.code || '';
  const message = String(error?.message || '');

  if (
    code === 'auth/invalid-credential' ||
    code === 'auth/invalid-login-credentials' ||
    code === 'auth/user-not-found' ||
    code === 'auth/wrong-password' ||
    message.includes('auth/invalid-credential') ||
    message.includes('auth/invalid-login-credentials')
  ) {
    return 'ログインできません。メールアドレスまたはパスワードをご確認ください。';
  }
  if (code === 'auth/invalid-email') {
    return 'メールアドレスの形式が正しくありません。';
  }
  if (code === 'auth/too-many-requests') {
    return 'ログイン試行回数が多すぎます。しばらく待ってから再度お試しください。';
  }
  if (code === 'auth/user-disabled') {
    return 'このアカウントは利用できません。';
  }
  if (code === 'auth/network-request-failed') {
    return 'ネットワークエラーが発生しました。接続を確認して再度お試しください。';
  }
  return 'ログインできません。';
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  if (!auth) {
    throw new Error('Firebase認証が初期化されていません');
  }
  const trimmed = email.trim();
  if (!trimmed) {
    throw new Error('メールアドレスを入力してください。');
  }

  try {
    auth.languageCode = 'ja';
    await sendPasswordResetEmail(auth, trimmed);
  } catch (error: any) {
    console.error('パスワード再設定メール送信エラー:', error);
    if (error.code === 'auth/invalid-email') {
      throw new Error('メールアドレスの形式が正しくありません。');
    }
    if (error.code === 'auth/too-many-requests') {
      throw new Error('送信回数が多すぎます。しばらく待ってから再度お試しください。');
    }
    if (error.code === 'auth/user-not-found') {
      return;
    }
    throw new Error('パスワード再設定メールを送信できませんでした。しばらく待ってから再度お試しください。');
  }
};

// メール/パスワードでサインアップ
export const signUpWithEmail = async (email: string, password: string): Promise<User> => {
  try {
    console.log('🔍 Firebase - メールサインアップ開始');
    if (!auth) {
      throw new Error('Firebase認証が初期化されていません');
    }
    
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log('🔍 Firebase - メールサインアップ成功:', {
      uid: result.user.uid,
      email: result.user.email
    });
    
    // 新規ユーザーのデフォルトプランを設定
    if (db) {
      const defaultPlan: UserPlan = {
        plan: 'free',
        creditsUsed: 0,
        creditsRest: 3,
        purchaseDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };
      await setDoc(doc(db, 'users', result.user.uid), defaultPlan);
      console.log('✅ デフォルトプラン設定完了');
    }
    
    return result.user;
  } catch (error: any) {
    console.error('メールサインアップエラー:', error);
    // エラーメッセージを日本語化
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('このメールアドレスは既に使用されています');
    } else if (error.code === 'auth/invalid-email') {
      throw new Error('メールアドレスの形式が正しくありません');
    } else if (error.code === 'auth/weak-password') {
      throw new Error('パスワードは6文字以上で設定してください');
    }
    throw error;
  }
};

// ログアウト
export const signOut = async (): Promise<void> => {
  try {
    console.log('🔍 Firebase - ログアウト開始');
    if (!auth) {
      throw new Error('Firebase認証が初期化されていません');
    }
    
    const currentUser = auth.currentUser;
    console.log('🔍 Firebase - 現在のユーザー:', currentUser ? `UID: ${currentUser.uid}` : 'なし');
    
    // ログアウト前にユーザー情報をローカルストレージに保存
    if (currentUser) {
      try {
        // 現在のユーザープラン情報を取得
        if (db) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userPlan = userDoc.data() as UserPlan;
            const userData = {
              uid: currentUser.uid,
              email: currentUser.email,
              displayName: currentUser.displayName,
              userPlan: userPlan,
              lastLogin: new Date().toISOString()
            };
            
            localStorage.setItem('savedUserData', JSON.stringify(userData));
            console.log('💾 ユーザー情報をローカルストレージに保存:', {
              uid: currentUser.uid,
              email: currentUser.email,
              plan: userPlan.plan,
              creditsRest: userPlan.creditsRest
            });
          }
        }
      } catch (error) {
        console.warn('⚠️ ユーザー情報保存エラー:', error);
      }
    }
    
    // Firebaseのセッションを確実に消す
    await firebaseSignOut(auth);
    console.log('🔍 Firebase - ログアウト完了');
    
    // ギャラリーとフリートライアル情報のみクリア（ユーザー情報は保持）
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('gallery_') || key.startsWith('freeTrial'))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🔍 Firebase - 削除: ${key}`);
    });
    
    // セッションストレージもクリア
    sessionStorage.clear();
    
    // ページをリロードして完全にリセット
    console.log('🔍 Firebase - ページリロードで完全リセット');
    window.location.reload();
    
    console.log('🔍 Firebase - 完全クリーンアップ完了');
  } catch (error) {
    console.error('ログアウトエラー:', error);
    throw error;
  }
};

// 保存されたユーザー情報を復元（ログイン必須のため簡素化）
export const restoreSavedUserData = async (): Promise<{ user: User; userPlan: UserPlan } | null> => {
  try {
    console.log('🔍 restoreSavedUserData開始');
    const savedData = localStorage.getItem('savedUserData');
    if (!savedData) {
      console.log('🔍 保存されたユーザー情報なし');
      return null;
    }

    const userData = JSON.parse(savedData);
    console.log('🔍 保存されたユーザー情報を発見:', {
      uid: userData.uid,
      email: userData.email,
      plan: userData.userPlan?.plan,
      creditsRest: userData.userPlan?.creditsRest
    });

    // 復元されたユーザーとして復元（実際の認証は行わない）
    const restoredUser = {
      uid: userData.uid,
      email: userData.email,
      displayName: userData.displayName,
      isAnonymous: false, // ログイン必須のため匿名は廃止
      emailVerified: true, // ログイン必須のため認証済み
      photoURL: null,
      phoneNumber: null,
      providerData: [],
      metadata: {
        creationTime: userData.lastLogin,
        lastSignInTime: userData.lastLogin
      },
      providerId: 'firebase',
      refreshToken: '',
      tenantId: null,
      delete: async () => {},
      getIdToken: async () => '',
      getIdTokenResult: async () => ({} as any),
      reload: async () => {},
      toJSON: () => ({})
    } as User;

    return {
      user: restoredUser,
      userPlan: userData.userPlan
    };
  } catch (error) {
    console.error('❌ 保存されたユーザー情報復元エラー:', error);
    return null;
  }
};

// ユーザープラン情報を取得
export const getUserPlan = async (uid: string): Promise<UserPlan | null> => {
  try {
    if (!db) {
      console.warn('Firestoreが初期化されていません');
      return null;
    }
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const planData = userDoc.data() as UserPlan;
      // FirestoreにcreditsRestが無い場合（旧Webhook等）は計算して補う
      const planWithRest = addCreditsRestToPlan(planData);
      console.log('💳 getUserPlan - Direct fetch:', {
        creditsUsed: planData.creditsUsed,
        plan: planData.plan,
        creditsRest: planWithRest.creditsRest,
        userId: uid,
        timestamp: new Date().toISOString()
      });
      return planWithRest;
    }
    return null;
  } catch (error) {
    console.error('ユーザープラン取得エラー:', error);
    return null;
  }
};

// クレジット情報を強制的に同期する関数
// ユーザープランを手動で修正する関数（デバッグ用）
export const fixUserPlan = async (uid: string, planType: 'standard' | 'pro' | 'pro_max') => {
  if (!db) {
    console.error('Firestoreが初期化されていません');
    return null;
  }

  try {
    console.log(`🔧 ユーザープランを修正中: ${uid} -> ${planType}`);
    
    const planLimits = {
      standard: 10,
      pro: 25,
      pro_max: 200
    };

    const updatedPlan: UserPlan = {
      plan: planType,
      creditsUsed: 0,
      purchaseDate: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      isSubscription: true
    };

    await setDoc(doc(db, 'users', uid), updatedPlan, { merge: true });
    console.log(`✅ ユーザープラン修正完了: ${planType}`);
    
    return updatedPlan;
  } catch (error) {
    console.error('❌ ユーザープラン修正エラー:', error);
    return null;
  }
};

export const forceSyncCredits = async (uid: string): Promise<UserPlan | null> => {
  try {
    console.log('🔄 Force syncing credits for user:', uid);
    const plan = await getUserPlan(uid);
    if (plan) {
      console.log('✅ Credits synced:', {
        creditsUsed: plan.creditsUsed,
        plan: plan.plan,
        userId: uid
      });
    }
    return plan;
  } catch (error) {
    console.error('❌ Force sync failed:', error);
    return null;
  }
};

// クレジットをチェックする関数（消費しない）
export const checkCredits = async (uid: string): Promise<{ hasCredits: boolean; remainingCredits: number; plan: UserPlan | null }> => {
  try {
    if (!db) {
      throw new Error('Firebase が初期化されていません');
    }

    const userDocRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // ユーザードキュメントが存在しない場合は新規作成可能
      return { hasCredits: true, remainingCredits: 3, plan: { plan: 'free', creditsRest: 3 } };
    }
    
    const currentData = userDoc.data() as UserPlan;
    const remainingCredits = currentData.creditsRest || 0;
    
    console.log('💳 Credit check (no consumption):', {
      remainingCredits,
      plan: currentData.plan,
      userId: uid
    });
    
    return { 
      hasCredits: remainingCredits > 0, 
      remainingCredits: Math.max(0, remainingCredits), 
      plan: currentData 
    };
  } catch (error) {
    console.error('❌ Credit check failed:', error);
    throw error;
  }
};

// クレジットをアトミックにチェック・消費する関数
export const atomicConsumeCredit = async (uid: string): Promise<{ success: boolean; remainingCredits: number; plan: UserPlan | null }> => {
  try {
    if (!db) {
      throw new Error('Firebase が初期化されていません');
    }

    const userDocRef = doc(db, 'users', uid);
    
    console.log('💳 Atomic credit consumption attempt for user:', uid);
    
    // トランザクションを使用してクレジットを安全にチェック・消費
    const result = await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userDocRef);
      
      if (!userDoc.exists()) {
        // ユーザードキュメントが存在しない場合は新規作成（無料プラン）
        console.log('💳 Creating new user document (free plan)');
        const newPlan = {
          plan: 'free' as const,
          creditsRest: 2 // 3回のうち1回消費して2回残り
        };
        transaction.set(userDocRef, newPlan);
        return { success: true, remainingCredits: 2, plan: newPlan };
      }
      
      const currentData = userDoc.data() as UserPlan;
      const currentCreditsRest = currentData.creditsRest || 0;
      
      console.log('💳 Credit check:', {
        currentCreditsRest,
        plan: currentData.plan,
        userId: uid
      });
      
      if (currentCreditsRest <= 0) {
        // クレジットが不足している場合
        console.log('❌ Insufficient credits:', { currentCreditsRest });
        return { success: false, remainingCredits: 0, plan: currentData };
      }
      
      // クレジットを消費（creditsRestのみ更新）
      const newCreditsRest = currentCreditsRest - 1;
      const updatedPlan = {
        ...currentData,
        creditsRest: newCreditsRest
      };
      
      console.log('✅ Credit consumed:', {
        oldCreditsRest: currentCreditsRest,
        newCreditsRest,
        userId: uid
      });
      
      transaction.update(userDocRef, {
        creditsRest: newCreditsRest
      });
      
      return { 
        success: true, 
        remainingCredits: newCreditsRest, 
        plan: updatedPlan 
      };
    });
    
    return result;
  } catch (error) {
    console.error('❌ Atomic credit consumption failed:', error);
    throw error;
  }
};

// ユーザープラン情報を設定
export const setUserPlan = async (uid: string, plan: UserPlan): Promise<void> => {
  try {
    if (!db) {
      throw new Error('Firestoreが初期化されていません');
    }
    await setDoc(doc(db, 'users', uid), plan, { merge: true });
  } catch (error) {
    console.error('ユーザープラン設定エラー:', error);
    throw error;
  }
};

// クレジットを追加する関数（ワンタイム購入用）
export const addCredits = async (uid: string, planType: 'standard' | 'pro'): Promise<UserPlan | null> => {
  try {
    if (!db) {
      throw new Error('Firestoreが初期化されていません');
    }

    console.log(`💳 クレジット追加開始: ${planType} for user ${uid}`);

    const userDocRef = doc(db, 'users', uid);
    
    // トランザクションを使用してクレジットを安全に追加
    const result = await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('ユーザードキュメントが存在しません');
      }
      
      const currentData = userDoc.data() as UserPlan;
      
      // プランに応じたクレジット数を取得
      let creditsToAdd = 0;
      switch (planType) {
        case 'standard':
          creditsToAdd = 10;
          break;
        case 'pro':
          creditsToAdd = 25;
          break;
        default:
          throw new Error('無効なプランタイプです');
      }
      
      const newCreditsRest = (currentData.creditsRest || 0) + creditsToAdd;
      
      // プラン優先順位: Pro > Standard > Free
      // 既存プランが新しいプランより高い場合は維持
      const planPriority = { 'pro': 3, 'standard': 2, 'free': 1 };
      const currentPlanPriority = planPriority[currentData.plan as keyof typeof planPriority] || 1;
      const newPlanPriority = planPriority[planType];
      
      const finalPlan = newPlanPriority > currentPlanPriority ? planType : currentData.plan;
      
      const updatedPlan: UserPlan = {
        ...currentData,
        plan: finalPlan, // プラン優先順位を考慮
        creditsRest: newCreditsRest,
        purchaseDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        isSubscription: false // ワンタイム購入
      };
      
      console.log('💳 クレジット追加詳細:', {
        planType,
        creditsToAdd,
        currentCreditsRest: currentData.creditsRest || 0,
        newCreditsRest,
        currentPlan: currentData.plan,
        finalPlan,
        planPriorityChange: newPlanPriority > currentPlanPriority ? 'アップグレード' : '維持',
        userId: uid
      });
      
      transaction.update(userDocRef, {
        plan: updatedPlan.plan,
        creditsRest: updatedPlan.creditsRest,
        purchaseDate: updatedPlan.purchaseDate,
        lastUpdated: updatedPlan.lastUpdated
      });
      
      return updatedPlan;
    });
    
    console.log(`✅ クレジット追加完了: ${planType} for user ${uid}`);
    return result;
    
  } catch (error) {
    console.error('❌ クレジット追加エラー:', error);
    throw error;
  }
};

// 生成された画像情報の型定義
export interface GeneratedImage {
  id: string;
  imageUrl?: string; // Firebase Storage URL（新形式）
  imageData?: string; // base64データ（旧形式、後方互換性のため）
  imagePath?: string; // Storage上のパス（削除時に使用）
  mimeType: string;
  prompt: string;
  template?: string;
  outfit?: string;
  pose?: string;
  customBackground?: string;
  createdAt: string;
  thumbnail?: string; // サムネイル用の小さな画像データ
}

// 画像をJPEGに変換する関数（ファイルサイズ削減のため）
const convertToJPEG = (base64Image: string, quality: number = 0.95): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      
      // 白背景を描画（JPEGは透過非対応のため）
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // 画像を描画
      ctx.drawImage(img, 0, 0);
      
      // JPEG形式でdata URLを取得（品質90%）
      const jpegDataUrl = canvas.toDataURL('image/jpeg', quality);
      
      console.log('📊 画像変換:', {
        元のサイズ: base64Image.length,
        JPEG後のサイズ: jpegDataUrl.length,
        削減率: `${Math.round((1 - jpegDataUrl.length / base64Image.length) * 100)}%`
      });
      
      resolve(jpegDataUrl);
    };
    
    img.onerror = () => {
      reject(new Error('画像の読み込みに失敗しました'));
    };
    
    img.src = base64Image;
  });
};

// クレジット使用量を更新し、生成された画像を保存
export const updateCreditsAndSaveImage = async (
  uid: string, 
  imageData: string, 
  mimeType: string,
  prompt: string,
  template?: string,
  outfit?: string,
  pose?: string,
  customBackground?: string,
  settings?: any
): Promise<{ imageUrl: string; imageId: string }> => {
  try {
    console.log('🔍 updateCreditsAndSaveImage - Firebase初期化チェック:', {
      hasDb: !!db,
      hasStorage: !!storage,
      dbType: typeof db,
      storageType: typeof storage
    });
    
    if (!db || !storage) {
      throw new Error('Firebase が初期化されていません');
    }
    
    // アトミックにクレジットをチェック・消費
    const creditResult = await atomicConsumeCredit(uid);
    
    if (!creditResult.success) {
      throw new Error(`クレジットが不足しています。残りクレジット: ${creditResult.remainingCredits}`);
    }
    
    console.log('✅ Credit consumed successfully:', {
      remainingCredits: creditResult.remainingCredits,
      plan: creditResult.plan?.plan
    });
    
    // Firebaseに保存されているので、ローカルストレージの更新は不要
    
    // 生成された画像をStorageに保存
    const imageId = `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // 画像をJPEGに変換してファイルサイズを削減
    let finalImageData = imageData;
    let finalMimeType = 'image/jpeg'; // JPEGに統一
    let fileExtension = 'jpg';
    
    try {
      // Canvas を使用して JPEG に変換（品質95%）
      finalImageData = await convertToJPEG(imageData, 0.95);
      console.log('🖼️ 画像をJPEGに変換しました（品質95%）');
    } catch (conversionError) {
      console.warn('⚠️ JPEG変換に失敗、元の形式で保存します:', conversionError);
      finalMimeType = mimeType || 'image/png';
      fileExtension = finalMimeType.includes('jpeg') || finalMimeType.includes('jpg') ? 'jpg' : 'png';
    }
    
    const imagePath = `users/${uid}/generated/${imageId}.${fileExtension}`;
    const storageRef = ref(storage, imagePath);
    
    console.log('🗂️ Uploading image to Storage:', imagePath);
    
    // base64データからdata:image/...の部分を除去してアップロード
    const base64Data = finalImageData.includes(',') ? finalImageData.split(',')[1] : finalImageData;
    await uploadString(storageRef, base64Data, 'base64', {
      contentType: finalMimeType
    });
    
    // ダウンロードURLを取得
    const imageUrl = await getDownloadURL(storageRef);
    console.log('✅ Image uploaded successfully, URL:', imageUrl.substring(0, 100) + '...');
    
    // Firestoreに画像メタデータとURLを保存（画像データ本体は保存しない）
    const generatedImage = {
      id: imageId,
      imageUrl, // StorageのURL
      imagePath, // Storage上のパス（削除時に使用）
      mimeType: finalMimeType, // 変換後のMIMEタイプを使用
      prompt,
      template,
      outfit,
      pose,
      customBackground,
      settings: settings || null, // 再生成用の設定情報
      createdAt: new Date().toISOString()
    };
    
    console.log('📋 Firestore保存データ:', {
      imageId,
      template,
      outfit,
      pose,
      hasSettings: !!settings,
      settingsKeys: settings ? Object.keys(settings) : 'none',
      settingsSize: settings ? JSON.stringify(settings).length : 0
    });
    
    // ユーザーの画像コレクションに保存
    const imageDocRef = doc(db, 'users', uid, 'generatedImages', imageId);
    await setDoc(imageDocRef, generatedImage);
    
    console.log('✅ 画像URLとメタデータをFirestoreに保存しました:', imageId);
    
    // Storage URLと画像IDを返す
    return { imageUrl, imageId };
  } catch (error) {
    console.error('❌ クレジット使用量更新・画像保存エラー:', error);
    throw error;
  }
};

// ユーザーの生成された画像一覧を取得
export const getUserGeneratedImages = async (
  uid: string,
  options: { limitCount?: number } = {},
): Promise<GeneratedImage[]> => {
  try {
    if (!db) {
      console.warn('Firestoreが初期化されていません');
      return [];
    }

    const imagesRef = collection(db, 'users', uid, 'generatedImages');
    // createdAt 降順。limit 指定時は最新 N 件のみ（ギャラリー全体は無制限）
    let q = query(imagesRef, orderBy('createdAt', 'desc'));
    if (options.limitCount && options.limitCount > 0) {
      q = query(imagesRef, orderBy('createdAt', 'desc'), limit(options.limitCount));
    }

    const snapshot = await getDocs(q);

    const images: GeneratedImage[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as GeneratedImage;
      images.push({ ...data, id: data.id || docSnap.id });
    });

    return images;
  } catch (error) {
    // orderBy 用インデックス未整備時は全件取得にフォールバック
    console.warn('生成画像一覧（orderBy）失敗、全件フォールバック:', error);
    try {
      const imagesRef = collection(db, 'users', uid, 'generatedImages');
      const snapshot = await getDocs(imagesRef);
      const images: GeneratedImage[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as GeneratedImage;
        images.push({ ...data, id: data.id || docSnap.id });
      });
      images.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      if (options.limitCount && options.limitCount > 0) {
        return images.slice(0, options.limitCount);
      }
      return images;
    } catch (fallbackError) {
      console.error('生成画像一覧取得エラー:', fallbackError);
      return [];
    }
  }
};

// 最新の生成画像のStorage URLを取得
export const getLatestGeneratedImageUrl = async (uid: string): Promise<string | null> => {
  try {
    const images = await getUserGeneratedImages(uid);
    if (images.length > 0 && images[0].imageUrl) {
      console.log('📸 Latest image URL:', images[0].imageUrl.substring(0, 100) + '...');
      return images[0].imageUrl;
    }
    return null;
  } catch (error) {
    console.error('最新画像URL取得エラー:', error);
    return null;
  }
};

// 生成された画像を削除
export const deleteGeneratedImage = async (uid: string, imageId: string): Promise<void> => {
  try {
    if (!db || !storage) {
      throw new Error('Firebase が初期化されていません');
    }
    
    // Firestoreから画像メタデータを取得
    const imageDocRef = doc(db, 'users', uid, 'generatedImages', imageId);
    const imageDoc = await getDoc(imageDocRef);
    
    if (imageDoc.exists()) {
      const imageData = imageDoc.data();
      
      // Storageから画像を削除
      if (imageData.imagePath) {
        try {
          const storageRef = ref(storage, imageData.imagePath);
          await deleteObject(storageRef);
          console.log('🗑️ Storage から画像を削除しました:', imageData.imagePath);
        } catch (storageError) {
          console.warn('⚠️ Storage削除エラー（続行）:', storageError);
          // Storageの削除に失敗してもFirestoreは削除する
        }
      }
    }
    
    // Firestoreから画像メタデータを削除
    await deleteDoc(imageDocRef);
    console.log('✅ Firestore から画像メタデータを削除しました:', imageId);
  } catch (error) {
    console.error('❌ 画像削除エラー:', error);
    throw error;
  }
};

// ユーザープラン情報をリアルタイム購読
export const subscribeToUserPlan = (
  uid: string,
  callback: (plan: UserPlan | null) => void
): Unsubscribe => {
  if (!db) {
    console.warn('Firestoreが初期化されていません');
    // デフォルトのfreeプランを即座に返す
    const defaultPlan = {
      plan: 'free' as const,
      creditsUsed: 0
    };
    callback(defaultPlan);
    return () => {}; // 空のunsubscribe関数を返す
  }
  
  console.log('subscribeToUserPlan: 購読開始', uid);
  
  // 接続リトライ機能を追加
  let retryCount = 0;
  const maxRetries = 5; // リトライ回数を増加
  let isCompleted = false;
  
  // より長いタイムアウトで確実にプラン情報を取得
  const timeoutId = setTimeout(() => {
    if (!isCompleted) {
      console.warn('subscribeToUserPlan: タイムアウト - オフラインプランを確認します');
      isCompleted = true;
      
      const offlinePlan = getOfflineUserPlan(uid);
      if (offlinePlan) {
        console.log('subscribeToUserPlan: オフラインプランを使用', offlinePlan);
        callback(offlinePlan);
      } else {
        console.warn('subscribeToUserPlan: オフラインプランもなし - コールバックを送らない（仮の0を避ける）');
      }
    }
  }, 15000); // 15秒に延長
  
  const unsubscribe = onSnapshot(
    doc(db, 'users', uid),
    (docSnap) => {
      clearTimeout(timeoutId); // 成功したらタイムアウトをクリア
      isCompleted = true;
      console.log('subscribeToUserPlan: ドキュメント受信', docSnap.exists());
      if (docSnap.exists()) {
        const planData = docSnap.data() as UserPlan;
        const planWithRest = addCreditsRestToPlan(planData);
        console.log('🔍 subscribeToUserPlan: プランデータ受信成功', {
          planType: planData.plan,
          creditsUsed: planData.creditsUsed,
          creditsRest: planWithRest.creditsRest,
          isPaidPlan: planData.plan !== 'free',
          userId: uid,
          fromCache: docSnap.metadata.fromCache,
          hasPendingWrites: docSnap.metadata.hasPendingWrites,
        });
        callback(planWithRest);
      } else {
        const offlinePlan = getOfflineUserPlan(uid);
        if (offlinePlan) {
          console.log('⚠️ ユーザードキュメントなし - オフラインプランを維持');
          callback(offlinePlan);
          return;
        }
        console.log('⚠️ ユーザードキュメントが存在しないため、デフォルトのfreeプランを設定します');
        callback(addCreditsRestToPlan({
          plan: 'free',
          creditsUsed: 0,
          creditsRest: 3,
        }));
      }
    },
    (error) => {
      clearTimeout(timeoutId);
      console.error('ユーザープラン購読エラー:', error);
      
      if (retryCount < maxRetries) {
        retryCount++;
        console.log(`subscribeToUserPlan: リトライ ${retryCount}/${maxRetries}`);
        setTimeout(() => {
          subscribeToUserPlan(uid, callback);
        }, 2000 * retryCount);
        return;
      }
      
      const offlinePlan = getOfflineUserPlan(uid);
      if (offlinePlan) {
        console.warn('subscribeToUserPlan: リトライ上限 - オフラインプランを使用');
        callback(offlinePlan);
        return;
      }
      console.warn('subscribeToUserPlan: リトライ上限 - 仮プランは送らない');
    }
  );
  
  // クリーンアップ関数を返す
  return () => {
    clearTimeout(timeoutId);
    unsubscribe();
  };
};

// 匿名ユーザー機能は廃止 - ログイン必須に変更

// 匿名ユーザー機能は廃止 - ログイン必須に変更

// 匿名ユーザー機能は廃止 - ログイン必須に変更
// 匿名ユーザー機能は廃止 - ログイン必須に変更

// 匿名ユーザー機能は廃止 - ログイン必須に変更
export const onAuthStateChange = (callback: (user: User | null) => void): Unsubscribe => {
  if (!auth) {
    console.warn('Firebase認証が初期化されていません');
    callback(null);
    return () => {}; // 空のunsubscribe関数を返す
  }
  return onAuthStateChanged(auth, callback);
};

// ショーケース関連の関数
export interface ShowcaseItem {
  id: string;
  imageUrl: string;
  prompt: string;
  createdAt: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  settings?: any;
  isPublic: boolean;
  imageId?: string; // ギャラリー画像IDとの紐づけ用
}

const SHOWCASE_WRITE_TIMEOUT_MS = 8000;
const MAX_SHOWCASE_PROMPT = 8000;
const MAX_SHOWCASE_SETTING_STRING = 200;
/** ポートレート等の自由記述プロンプト用 */
const MAX_SHOWCASE_PROMPT_SETTING_STRING = 4000;
const LONG_TEXT_SETTING_KEYS = new Set([
  'userPrompt',
  'customBackground',
  'customOutfitText',
  'customBackgroundText',
  'customDressText',
  'customSleeveText',
  'customItemText',
]);

/** Firestore に書く最小限の settings だけ残す */
const sanitizeShowcaseSettings = (settings: any): Record<string, unknown> | null => {
  if (!settings || typeof settings !== 'object') {
    return null;
  }

  const allowKeys = new Set([
    'mode',
    'background',
    'backgroundOption',
    'backgroundId',
    'outfit',
    'selectedOutfitId',
    'dressId',
    'pose',
    'selectedPoseId',
    'poseId',
    'sleeveId',
    'itemId',
    // 自由記述プロンプト（「このスタイルで」再表示用）
    'userPrompt',
    'customBackground',
    'customOutfitText',
    'customBackgroundText',
    'customDressText',
    'customSleeveText',
    'customItemText',
  ]);

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(settings)) {
    if (!allowKeys.has(key)) continue;
    if (value === undefined || value === null) continue;

    if (typeof value === 'string') {
      if (value.startsWith('data:image/')) continue;
      const maxLen = LONG_TEXT_SETTING_KEYS.has(key)
        ? MAX_SHOWCASE_PROMPT_SETTING_STRING
        : MAX_SHOWCASE_SETTING_STRING;
      if (value.length > maxLen) {
        sanitized[key] = value.slice(0, maxLen);
        continue;
      }
      sanitized[key] = value;
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
    }
  }

  return Object.keys(sanitized).length > 0 ? sanitized : null;
};

const toFirestoreValue = (value: unknown): Record<string, unknown> => {
  if (value === null) return { nullValue: null };
  if (typeof value === 'string') return { stringValue: value };
  if (typeof value === 'boolean') return { booleanValue: value };
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? { integerValue: String(value) }
      : { doubleValue: value };
  }
  if (Array.isArray(value)) {
    return { arrayValue: { values: value.map(toFirestoreValue) } };
  }
  if (typeof value === 'object') {
    const fields: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      if (v !== undefined) fields[k] = toFirestoreValue(v);
    });
    return { mapValue: { fields } };
  }
  return { stringValue: String(value) };
};

const toFirestoreDocumentFields = (data: Record<string, unknown>) => {
  const fields: Record<string, unknown> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined) {
      fields[key] = toFirestoreValue(value);
    }
  });
  return fields;
};

/** SDKのlong pollingを避け、RESTで直接書き込む（タイムアウト対策） */
const createShowcaseViaRest = async (
  showcaseId: string,
  data: Record<string, unknown>,
  idToken: string,
): Promise<void> => {
  const url =
    `https://firestore.googleapis.com/v1/projects/${firebaseConfig.projectId}` +
    `/databases/(default)/documents/showcase?documentId=${encodeURIComponent(showcaseId)}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), SHOWCASE_WRITE_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fields: toFirestoreDocumentFields(data) }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      const message =
        (errBody as any)?.error?.message ||
        `Firestore REST書き込み失敗 (${response.status})`;
      const error = new Error(message) as Error & { code?: string };
      if (response.status === 403) error.code = 'permission-denied';
      throw error;
    }
  } finally {
    clearTimeout(timer);
  }
};

/** Cloud Function 経由の追加（REST失敗時のフォールバック） */
const createShowcaseViaCallable = async (
  data: {
    imageUrl: string;
    prompt: string;
    settings: Record<string, unknown> | null;
    imageId?: string;
  },
): Promise<string> => {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const functions = getFunctions(getApp(), 'us-central1');
  const callable = httpsCallable(functions, 'addToShowcase');
  const result = await Promise.race([
    callable(data),
    new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error('ショーケースへの追加がタイムアウトしました。通信状況を確認して再度お試しください。')),
        SHOWCASE_WRITE_TIMEOUT_MS,
      );
    }),
  ]) as { data: { showcaseId?: string; success?: boolean } };

  const showcaseId = result?.data?.showcaseId;
  if (!showcaseId) {
    throw new Error('ショーケースIDの取得に失敗しました');
  }
  return showcaseId;
};

// ショーケースに作品を追加
export const addToShowcase = async (
  imageUrl: string,
  prompt: string,
  settings?: any,
  imageId?: string
): Promise<string> => {
  try {
    if (!auth?.currentUser) {
      throw new Error('ユーザーが認証されていません');
    }

    if (!imageUrl || typeof imageUrl !== 'string') {
      throw new Error('画像URLが無効です');
    }

    if (imageUrl.startsWith('data:image/')) {
      throw new Error('この画像はショーケースに追加できません。有料プランで保存された画像をお使いください。');
    }

    if (!/^https?:\/\//i.test(imageUrl)) {
      throw new Error('画像URLが無効です。もう一度生成してからお試しください。');
    }

    const user = auth.currentUser;
    const showcaseId = `showcase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const safeSettings = sanitizeShowcaseSettings(settings);
    const safePrompt = (prompt || '生成された宣材写真').slice(0, MAX_SHOWCASE_PROMPT);

    const showcaseItem: Record<string, unknown> = {
      id: showcaseId,
      imageUrl,
      prompt: safePrompt,
      createdAt: new Date().toISOString(),
      userId: user.uid,
      userName: user.displayName || 'Anonymous',
      userEmail: user.email || '',
      isPublic: true,
    };

    if (safeSettings) {
      showcaseItem.settings = safeSettings;
    }
    if (imageId) {
      showcaseItem.imageId = imageId;
    }

    const startedAt = performance.now();
    const idToken = await user.getIdToken();

    try {
      await createShowcaseViaRest(showcaseId, showcaseItem, idToken);
      console.log(`✅ ショーケース追加完了(REST): ${showcaseId} (${Math.round(performance.now() - startedAt)}ms)`);
    } catch (restError) {
      console.warn('⚠️ Showcase REST write failed, trying Cloud Function:', restError);
      const viaFnId = await createShowcaseViaCallable({
        imageUrl,
        prompt: safePrompt,
        settings: safeSettings,
        imageId,
      });
      console.log(`✅ ショーケース追加完了(Functions): ${viaFnId} (${Math.round(performance.now() - startedAt)}ms)`);
    try {
      sessionStorage.removeItem('profileStudio_showcaseFeed_v2');
      sessionStorage.removeItem('profileStudio_showcaseFeed_v1');
      localStorage.removeItem('profileStudio_showcaseList_v3');
      localStorage.removeItem('profileStudio_showcaseList_v4');
    } catch {
      // ignore
    }
    return viaFnId;
    }

    try {
      sessionStorage.removeItem('profileStudio_showcaseFeed_v2');
      sessionStorage.removeItem('profileStudio_showcaseFeed_v1');
      localStorage.removeItem('profileStudio_showcaseList_v3');
      localStorage.removeItem('profileStudio_showcaseList_v4');
    } catch {
      // ignore
    }
    return showcaseId;
  } catch (error) {
    console.error('❌ ショーケース追加エラー詳細:', {
      errorMessage: error instanceof Error ? error.message : String(error),
      errorCode: (error as any)?.code,
    });
    throw error;
  }
};

// ショーケースから作品を削除
export const removeFromShowcase = async (showcaseId: string): Promise<void> => {
  try {
    if (!auth?.currentUser || !db) {
      throw new Error('認証またはFirestoreが初期化されていません');
    }

    const showcaseRef = doc(db, 'showcase', showcaseId);
    const showcaseDoc = await getDoc(showcaseRef);
    
    if (!showcaseDoc.exists()) {
      throw new Error('ショーケースアイテムが見つかりません');
    }

    const showcaseData = showcaseDoc.data() as ShowcaseItem;
    
    // 作品の所有者のみ削除可能
    if (showcaseData.userId !== auth.currentUser.uid) {
      throw new Error('この作品を削除する権限がありません');
    }

    await deleteDoc(showcaseRef);
    console.log('✅ ショーケースから削除完了:', showcaseId);
    try {
      const { invalidateShowcaseFeedCache } = await import('../utils/showcaseFeed');
      invalidateShowcaseFeedCache();
    } catch {
      // ignore cache invalidation errors
    }
  } catch (error) {
    console.error('❌ ショーケース削除エラー:', error);
    throw error;
  }
};

// ショーケース作品を1件取得
export const getShowcaseItemById = async (showcaseId: string): Promise<ShowcaseItem | null> => {
  try {
    if (!db) {
      throw new Error('Firestoreが初期化されていません');
    }
    const showcaseRef = doc(db, 'showcase', showcaseId);
    const showcaseDoc = await getDoc(showcaseRef);
    if (!showcaseDoc.exists()) {
      return null;
    }
    return showcaseDoc.data() as ShowcaseItem;
  } catch (error) {
    console.error('❌ ショーケース取得エラー:', error);
    throw error;
  }
};

// 全てのショーケース作品を取得
export const getAllShowcaseItems = async (): Promise<ShowcaseItem[]> => {
  try {
    console.log('🎨 ショーケース取得開始（公開読み取り）');

    if (!db) {
      throw new Error('Firestoreが初期化されていません');
    }

    // 公開されている作品のみ取得（認証不要）
    const showcaseCollection = collection(db, 'showcase');
    const showcaseSnapshot = await getDocs(showcaseCollection);
    
    const showcaseItems: ShowcaseItem[] = [];
    showcaseSnapshot.forEach((doc) => {
      const data = doc.data() as ShowcaseItem;
      if (data.isPublic) {
        showcaseItems.push(data);
      }
    });

    // 作成日時の降順でソート
    showcaseItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    
    console.log('✅ ショーケース作品取得完了:', showcaseItems.length);
    return showcaseItems;
  } catch (error) {
    console.error('❌ ショーケース取得エラー:', error);
    throw error;
  }
};

// ユーザーのショーケース作品を取得
export const getUserShowcaseItems = async (userId: string): Promise<ShowcaseItem[]> => {
  try {
    if (!db) {
      throw new Error('Firestoreが初期化されていません');
    }

    // orderBy なしなら複合インデックス不要（未デプロイでも動く）
    const showcaseQuery = query(
      collection(db, 'showcase'),
      where('userId', '==', userId),
    );
    const showcaseSnapshot = await getDocs(showcaseQuery);
    
    const userShowcaseItems: ShowcaseItem[] = showcaseSnapshot.docs.map(
      (docSnap) => {
        const data = docSnap.data() as ShowcaseItem;
        return { ...data, id: data.id || docSnap.id };
      },
    );

    userShowcaseItems.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    
    console.log('✅ ユーザーショーケース作品取得完了:', userShowcaseItems.length);
    return userShowcaseItems;
  } catch (error) {
    console.error('❌ ユーザーショーケース取得エラー:', error);
    throw error;
  }
};
